import { useEffect, useState } from "react";
import type { Rpc } from "../lib/rpc";

interface JobLog { at: string; msg: string }
interface Job {
  id: string;
  platform: string;
  accountId: string;
  action: string;
  state: "pending" | "in_progress" | "done" | "failed";
  createdAt: string;
  scheduledFor?: string | null;
  priority?: "now" | "normal";
  attempts?: number;
  lastError?: string | null;
  mediaPath?: string | null;
  log?: JobLog[];
}

interface PlatformSnapshot {
  platform: string;
  pending: Job[];
  in_progress: Job[];
  done: Job[];
  failed: Job[];
}

interface Account { id: string; displayName: string }

const STATE_LABEL: Record<Job["state"], string> = {
  pending: "Pending",
  in_progress: "Running",
  done: "Done",
  failed: "Failed",
};

function fmtTime(iso: string): string {
  try { return new Date(iso).toLocaleTimeString(); } catch { return iso; }
}
function fmtDate(iso: string): string {
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    return sameDay ? d.toLocaleTimeString() : d.toLocaleString();
  } catch { return iso; }
}

export function QueuePage({ rpc, electronConnected }: { rpc: Rpc; electronConnected: boolean }) {
  const [snapshots, setSnapshots] = useState<Record<string, PlatformSnapshot>>({});
  const [accounts, setAccounts] = useState<Record<string, string>>({});
  const [openJob, setOpenJob] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!electronConnected) return;
    let cancelled = false;
    async function loadAccounts() {
      try {
        const r = await rpc.call<{ accounts: Account[] }>("get-accounts");
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const a of r.accounts || []) map[a.id] = a.displayName;
        setAccounts(map);
      } catch { /* swallow */ }
    }
    async function refresh() {
      try {
        const r = await rpc.call<{ ok: boolean; snapshots?: Record<string, PlatformSnapshot> }>("jobs-snapshot");
        if (!cancelled && r.ok) setSnapshots(r.snapshots || {});
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    }
    loadAccounts();
    refresh();
    const handle = window.setInterval(refresh, 2000);
    const off = rpc.onEvent("jobs-changed", () => refresh());
    return () => { cancelled = true; window.clearInterval(handle); off(); };
  }, [rpc, electronConnected]);

  async function deleteJob(platform: string, id: string) {
    if (!confirm(`Delete job ${id}?`)) return;
    setBusy(`del:${id}`);
    try {
      await rpc.call("jobs-delete", platform, id);
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(null); }
  }
  async function retryJob(platform: string, id: string) {
    setBusy(`retry:${id}`);
    try {
      await rpc.call("jobs-retry", platform, id);
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(null); }
  }

  if (!electronConnected) {
    return (
      <section className="settings-page">
        <header className="settings-page-header"><h2>📦 Job Queue</h2></header>
        <div className="empty">Electron must be running on the worker PC.</div>
      </section>
    );
  }

  const platforms = Object.keys(snapshots).sort();

  return (
    <section className="settings-page">
      <header className="settings-page-header">
        <h2>📦 Job Queue</h2>
        {err && <span className="err">{err}</span>}
        <p className="hint">
          Per-(account × platform) post jobs. The Electron scheduler enqueues
          on schedule; workers claim them and run the pipeline. Auto-refreshes.
        </p>
      </header>

      {platforms.length === 0 && (
        <div className="empty">No platforms have any queue history yet.</div>
      )}

      {platforms.map((platform) => {
        const s = snapshots[platform];
        const counts = {
          pending: s.pending.length,
          running: s.in_progress.length,
          done: s.done.length,
          failed: s.failed.length,
        };
        return (
          <div key={platform} className="queue-platform">
            <header className="queue-platform-header">
              <h3>{platform.toUpperCase()}</h3>
              <div className="queue-counts">
                <span className="qc qc-pending">{counts.pending} pending</span>
                <span className="qc qc-running">{counts.running} running</span>
                <span className="qc qc-done">{counts.done} done</span>
                <span className="qc qc-failed">{counts.failed} failed</span>
              </div>
            </header>

            {(["in_progress", "pending", "failed", "done"] as const).map((state) => {
              const list = s[state];
              if (list.length === 0) return null;
              return (
                <div key={state} className={`queue-section queue-section-${state}`}>
                  <div className="queue-section-label">{STATE_LABEL[state]}</div>
                  {list.slice(0, state === "done" ? 8 : 25).map((job) => (
                    <JobRow
                      key={job.id}
                      job={job}
                      acctName={accounts[job.accountId] || job.accountId}
                      open={openJob === job.id}
                      busy={busy}
                      onToggle={() => setOpenJob(openJob === job.id ? null : job.id)}
                      onDelete={() => deleteJob(platform, job.id)}
                      onRetry={() => retryJob(platform, job.id)}
                    />
                  ))}
                  {list.length > (state === "done" ? 8 : 25) && (
                    <div className="muted small queue-more">
                      …and {list.length - (state === "done" ? 8 : 25)} more
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </section>
  );
}

function JobRow({
  job, acctName, open, busy, onToggle, onDelete, onRetry,
}: {
  job: Job; acctName: string; open: boolean; busy: string | null;
  onToggle: () => void; onDelete: () => void; onRetry: () => void;
}) {
  const isPending = job.state === "pending";
  const isFailed = job.state === "failed";
  const isDone = job.state === "done";

  return (
    <div className={`queue-row queue-row-${job.state}`}>
      <button className="queue-row-main" onClick={onToggle}>
        <span className="queue-row-acct">{acctName}</span>
        <span className="queue-row-priority">
          {job.priority === "now" ? "⚡ now" : ""}
        </span>
        <span className="queue-row-time">{fmtDate(job.createdAt)}</span>
        <span className="queue-row-attempt muted">
          {job.attempts ? `try ${job.attempts}` : ""}
        </span>
        {isFailed && job.lastError && (
          <span className="queue-row-error">{job.lastError.slice(0, 80)}</span>
        )}
        <span className="queue-row-chev">{open ? "▾" : "▸"}</span>
      </button>
      <div className="queue-row-actions">
        {(isPending) && (
          <button onClick={onDelete} disabled={busy !== null} className="danger">
            {busy === `del:${job.id}` ? "…" : "Cancel"}
          </button>
        )}
        {(isFailed || isDone) && (
          <>
            <button onClick={onRetry} disabled={busy !== null}>
              {busy === `retry:${job.id}` ? "…" : "Retry"}
            </button>
            <button onClick={onDelete} disabled={busy !== null} className="danger">
              {busy === `del:${job.id}` ? "…" : "Delete"}
            </button>
          </>
        )}
      </div>
      {open && (
        <div className="queue-row-detail">
          <div><strong>id</strong> <code>{job.id}</code></div>
          <div><strong>account</strong> {acctName} <span className="muted">({job.accountId})</span></div>
          <div><strong>action</strong> {job.action} <span className="muted">priority={job.priority || "normal"}</span></div>
          {job.scheduledFor && <div><strong>scheduled</strong> {fmtDate(job.scheduledFor)}</div>}
          {job.mediaPath && <div><strong>media</strong> <code className="path">{job.mediaPath}</code></div>}
          {job.lastError && <div className="err"><strong>last error</strong> {job.lastError}</div>}
          {job.log && job.log.length > 0 && (
            <div className="queue-log">
              <strong>log</strong>
              {job.log.slice(-15).map((l, i) => (
                <div key={i} className="queue-log-row">
                  <span className="muted small">{fmtTime(l.at)}</span> {l.msg}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
